import { Icon, ICONS } from 'components/Icon';
import { Pressable } from 'components/Pressable';
import { useGetThumbnail } from 'hooks/useGetThumbnail';
import * as React from 'react';
import { DimensionValue, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { Image, ImageContentFit } from 'expo-image';

type Props = {
  thumbnailId: string | undefined;
  style?: StyleProp<ViewStyle>;
  icon?: ICONS;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  width: DimensionValue;
  height: DimensionValue;
  contentFit?: ImageContentFit;
  onPress?: () => void;
  iconSize?: number;
} & (
  | {
      rounded?: boolean;
      borderRadius?: undefined;
    }
  | {
      rounded?: undefined;
      borderRadius: number;
    }
);

export const LocalThumbnail: React.FC<Props> = React.memo(
  ({
    icon = ICONS.ImageThin,
    rounded = false,
    borderRadius,
    width,
    height,
    iconSize,
    style,
    thumbnailId,
    accessibilityLabel,
    accessibilityHint,
    contentFit,
    onPress,
  }) => {
    const { image } = useGetThumbnail(thumbnailId || '') || {
      image: undefined,
    };

    const widthInt: number = Number.isInteger(width) ? (width as number) : 0;
    const heightInt: number = Number.isInteger(height) ? (height as number) : 0;
    const getIconSize = React.useCallback(
      (dimension: number) => Math.round(dimension * 0.6),
      [],
    );

    return (
      <Pressable
        style={[
          styles.container,
          {
            width,
            height,
            overflow: 'hidden',
            borderRadius: rounded ? 9999 : borderRadius,
          },
          style,
        ]}
        onPress={onPress && !!image ? onPress : undefined}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
      >
        {image ? (
          <Image
            source={{ uri: image }}
            style={{ width: '100%', height: '100%' }}
            contentFit={contentFit}
            contentPosition="center"
          />
        ) : (
          <Icon
            source={icon}
            size={
              iconSize ||
              (widthInt && heightInt
                ? getIconSize(Math.min(widthInt, heightInt))
                : widthInt
                  ? getIconSize(widthInt)
                  : heightInt
                    ? getIconSize(heightInt)
                    : getIconSize(80))
            }
            color="#bbb"
          />
        )}
      </Pressable>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ddd', // '#999',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
